/* packages/volto-castle-theme/src/components/Blocks/Contact/ContactForm.tsx */
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

type FormState = {
  name: string;
  email: string;
  subject: string;
  message: string;
  website: string; // honeypot
};

const INITIAL: FormState = {
  name: '',
  email: '',
  subject: '',
  message: '',
  website: '',
};

const apiBase = () => `${window.location.origin}/++api++`;

const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

function getQueryEmail(search: string): string {
  const params = new URLSearchParams(search);
  const raw = (params.get('email') || '').trim();
  return isEmail(raw) ? raw : '';
}

export default function ContactForm() {
  const location = useLocation();
  const history = useHistory();

  const [data, setData] = React.useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const nameInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    const qEmail = getQueryEmail(location.search);

    if (qEmail) {
      setData((d) => {
        if (d.email.trim()) return d; // don't overwrite if user already typed
        return { ...d, email: qEmail };
      });

      history.replace({
        pathname: location.pathname,
        search: '',
        hash: location.hash,
        state: location.state,
      });
    }

    requestAnimationFrame(() => {
      if (!sent) nameInputRef.current?.focus();
    });
  }, [history, location.hash, location.pathname, location.search, location.state, sent]);

  const onChange =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setData((d) => ({ ...d, [key]: e.target.value }));
    };

  const validate = () => {
    if (data.website.trim()) return 'Spam detected.';
    if (!data.name.trim()) return 'Please enter your name.';
    if (!isEmail(data.email.trim())) return 'Please enter a valid email.';
    if (!data.subject.trim()) return 'Please enter a subject.';
    if (data.message.trim().length < 10) return 'Message is too short.';
    return '';
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setSubmitting(true);
    setSent(false);

    try {
      const res = await fetch(`${apiBase()}/@contact`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: data.name.trim(),
          email: data.email.trim(),
          subject: data.subject.trim(),
          message: data.message.trim(),
          website: data.website.trim(),
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }

      setSent(true);
      setData(INITIAL);
      toast.success('Message sent!');
    } catch (e: any) {
      toast.error('Could not send message. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Contact submit failed:', e?.message || e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="contact-form" aria-label="Contact form">
      <div className="contact-form__container">
        <header className="contact-form__header">
          {/* <h2 className="contact-form__title">Contact</h2> */}
          <h3 className="contact-form__subtitle">Send us a message and we'll get back to you.</h3>
        </header>

        {sent ? (
          <div className="contact-form__success" role="status" aria-live="polite">
            Thanks! We'll get back to you shortly.
          </div>
        ) : null}

        <form className="contact-form__form" onSubmit={onSubmit}>
          <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
            <label>
              Website
              <input value={data.website} onChange={onChange('website')} autoComplete="off" />
            </label>
          </div>

          <div className="contact-form__row">
            <label className="contact-form__label">
              Name
              <input
                ref={nameInputRef}
                className="contact-form__input"
                value={data.name}
                onChange={onChange('name')}
                autoComplete="name"
                placeholder="Your name"
              />
            </label>

            <label className="contact-form__label">
              Email
              <input
                className="contact-form__input"
                value={data.email}
                onChange={onChange('email')}
                autoComplete="email"
                inputMode="email"
                placeholder="you@company.com"
              />
            </label>
          </div>

          <label className="contact-form__label">
            Subject
            <input
              className="contact-form__input"
              value={data.subject}
              onChange={onChange('subject')}
              placeholder="What’s this about?"
            />
          </label>

          <label className="contact-form__label">
            Message
            <textarea
              className="contact-form__textarea"
              value={data.message}
              onChange={onChange('message')}
              rows={6}
              placeholder="How can we help?"
            />
          </label>

          <button className="contact-form__button" type="submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send'}
          </button>
        </form>
      </div>
    </section>
  );
}